import React, { useState } from 'react';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  Filter,
  Download,
  BarChart3,
  Target,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  ComposedChart,
  Legend
} from 'recharts';

const CostAnalysis = ({ stats, loading }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedService, setSelectedService] = useState('all');

  const COLORS = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444'];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-full"></div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse h-80"></div>
          <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse h-80"></div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Нет данных для отображения</p>
      </div>
    );
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('ru-RU').format(value);
  };

  // Filter data based on selected service
  const costTrendData = (stats.costTrend || []).filter(item => 
    selectedService === 'all' || item.service === selectedService
  );

  const serviceCostData = (stats.serviceCosts || []).filter(item => 
    selectedService === 'all' || item.service === selectedService
  );

  const providerCostData = (stats.providerCosts || []).filter(item => 
    selectedService === 'all' || item.service === selectedService
  );

  const costEfficiencyData = stats.costEfficiency || [];
  const budgetUtilizationData = stats.budgetUtilization || [];
  const costProjectionsData = stats.costProjections || [];

  const totalCost = stats.totalCost || 0;
  const monthlyCost = stats.monthlyCost || 0;
  const costPerRequest = stats.costPerRequest || 0;
  const budgetRemaining = stats.budgetRemaining || 0;
  const budgetUtilization = stats.budgetUtilization || 0;

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-500" />
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="day">За день</option>
              <option value="week">За неделю</option>
              <option value="month">За месяц</option>
              <option value="quarter">За квартал</option>
              <option value="year">За год</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <select
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Все сервисы</option>
              <option value="llm">LLM</option>
              <option value="tts">TTS</option>
              <option value="stt">STT</option>
            </select>
          </div>
        </div>

        <button className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
          <Download className="w-4 h-4" />
          Экспорт отчета
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Общие затраты</h3>
          <p className="text-3xl font-bold text-gray-900 mb-2">{formatCurrency(totalCost)}</p>
          <div className="flex items-center gap-1 text-sm">
            {stats.costTrend >= 0 ? (
              <TrendingUp className="w-4 h-4 text-red-500" />
            ) : (
              <TrendingDown className="w-4 h-4 text-green-500" />
            )}
            <span className={stats.costTrend >= 0 ? 'text-red-600' : 'text-green-600'}>
              {Math.abs(stats.costTrend)}% за период
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <BarChart3 className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Месячные затраты</h3>
          <p className="text-3xl font-bold text-gray-900 mb-2">{formatCurrency(monthlyCost)}</p>
          <div className="flex items-center gap-1 text-sm">
            {stats.monthlyTrend >= 0 ? (
              <TrendingUp className="w-4 h-4 text-red-500" />
            ) : (
              <TrendingDown className="w-4 h-4 text-green-500" />
            )}
            <span className={stats.monthlyTrend >= 0 ? 'text-red-600' : 'text-green-600'}>
              {Math.abs(stats.monthlyTrend)}% к прошлому месяцу
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Target className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Стоимость запроса</h3>
          <p className="text-3xl font-bold text-gray-900 mb-2">{formatCurrency(costPerRequest)}</p>
          <div className="flex items-center gap-1 text-sm">
            {stats.costPerRequestTrend <= 0 ? (
              <TrendingDown className="w-4 h-4 text-green-500" />
            ) : (
              <TrendingUp className="w-4 h-4 text-red-500" />
            )}
            <span className={stats.costPerRequestTrend <= 0 ? 'text-green-600' : 'text-red-600'}>
              {Math.abs(stats.costPerRequestTrend)}% за период
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              {budgetUtilization > 80 ? (
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              ) : (
                <CheckCircle className="w-6 h-6 text-green-600" />
              )}
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Бюджет</h3>
          <p className="text-3xl font-bold text-gray-900 mb-2">{budgetUtilization}%</p>
          <div className="flex items-center gap-1 text-sm">
            <span className={budgetUtilization > 80 ? 'text-orange-600' : 'text-green-600'}>
              Осталось: {formatCurrency(budgetRemaining)}
            </span>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost Trend Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Динамика затрат</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={costTrendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                labelFormatter={(value) => new Date(value).toLocaleDateString('ru-RU')}
                formatter={(value) => [formatCurrency(value), 'Затраты']}
              />
              <Area 
                type="monotone" 
                dataKey="cost" 
                stroke="#3B82F6" 
                fill="#3B82F6"
                fillOpacity={0.3}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Service Cost Distribution */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Распределение затрат по сервисам</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={serviceCostData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="cost"
              >
                {serviceCostData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [formatCurrency(value), 'Затраты']} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Cost Efficiency Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Эффективность затрат</h3>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={costEfficiencyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="service" 
              tick={{ fontSize: 12 }}
            />
            <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
            <Tooltip 
              formatter={(value, name) => [
                name === 'costPerRequest' ? formatCurrency(value) : formatNumber(value),
                name === 'costPerRequest' ? 'Стоимость запроса' : 'Запросов'
              ]}
            />
            <Legend />
            <Bar yAxisId="left" dataKey="requests" fill="#10B981" name="Запросов" />
            <Line yAxisId="right" type="monotone" dataKey="costPerRequest" stroke="#EF4444" strokeWidth={2} name="Стоимость запроса" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Budget Utilization Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Использование бюджета</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={budgetUtilizationData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="service" 
              tick={{ fontSize: 12 }}
            />
            <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />
            <Tooltip 
              formatter={(value) => [`${value}%`, 'Использование бюджета']}
            />
            <Bar 
              dataKey="utilization" 
              fill={(entry) => entry.utilization > 80 ? '#EF4444' : entry.utilization > 60 ? '#F59E0B' : '#10B981'}
              name="Использование бюджета"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Cost Projections */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Прогноз затрат</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={costProjectionsData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="period" 
              tick={{ fontSize: 12 }}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip 
              formatter={(value, name) => [
                formatCurrency(value),
                name === 'projected' ? 'Прогноз' : 'Факт'
              ]}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="actual" 
              stroke="#3B82F6" 
              strokeWidth={2}
              name="Фактические затраты"
            />
            <Line 
              type="monotone" 
              dataKey="projected" 
              stroke="#8B5CF6" 
              strokeWidth={2}
              strokeDasharray="5 5"
              name="Прогноз"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Detailed Cost Table */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Детализация затрат</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Сервис
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Провайдер
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Запросов
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Затраты
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Стоимость запроса
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  % от общих затрат
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {providerCostData.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <span className="font-medium text-gray-900">{item.service}</span>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{item.provider}</td>
                  <td className="px-6 py-4 text-gray-900">{formatNumber(item.requests)}</td>
                  <td className="px-6 py-4 text-gray-900">{formatCurrency(item.cost)}</td>
                  <td className="px-6 py-4 text-gray-900">{formatCurrency(item.costPerRequest)}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{item.percentage}%</span>
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${item.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {providerCostData.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Нет данных для отображения
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CostAnalysis;
